import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getDashboardPath } from '../utils/authRoutes';
import { Mail, Lock, AlertCircle, Eye, EyeOff } from 'lucide-react';
import { AUTH } from '../constants/testIds';
import { isValidEmail, normalizeEmail } from '../utils/authForms';
import AuthLayout from '../components/auth/AuthLayout';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isProviderLogin = searchParams.get('role') === 'provider';
  const requestedProviderType = searchParams.get('type') || searchParams.get('provider_type');
  const roleType = isProviderLogin ? (requestedProviderType || 'provider') : 'client';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const cleanEmail = normalizeEmail(email);
    const cleanPassword = password.trim();

    if (!isValidEmail(cleanEmail)) {
      setError('Ingresa un correo válido.');
      return;
    }

    setEmail(cleanEmail);
    setPassword(cleanPassword);
    setLoading(true);

    const result = await login(cleanEmail, cleanPassword, isProviderLogin ? 'provider' : 'client');
    setLoading(false);

    if (result.success) {
      navigate(getDashboardPath(result.data), { replace: true });
    } else {
      setError(result.error);
    }
  };

  return (
    <AuthLayout mode="login" roleType={roleType} compact>
      <h2>{isProviderLogin ? 'Entrar al panel PROVI' : 'Iniciar sesión en PROVI'}</h2>
      <p className="subtitle">
        {isProviderLogin
          ? 'Accede a tus solicitudes, pedidos y herramientas de proveedor.'
          : 'Accede a tus viajes, pedidos y servicios locales.'}
      </p>

      {error && (
        <div className="provi-auth-error">
          <AlertCircle size={18} />
          <p>{error}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="provi-auth-form">
        <label className="provi-auth-field full" htmlFor="email">
          <span>Correo electrónico</span>
          <div className="provi-auth-input-wrap">
            <Mail size={19} />
            <input
              id="email"
              name="email"
              type="email"
              inputMode="email"
              autoComplete="email"
              data-testid={AUTH.loginEmailInput}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="tu@email.com"
            />
          </div>
        </label>

        <label className="provi-auth-field full" htmlFor="password">
          <span>Contraseña</span>
          <div className="provi-auth-input-wrap">
            <Lock size={19} />
            <input
              id="password"
              name="password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              data-testid={AUTH.loginPasswordInput}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              placeholder="********"
            />
            <button
              type="button"
              onClick={() => setShowPassword((current) => !current)}
              className="provi-auth-eye"
              aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
        </label>

        <button
          type="submit"
          data-testid={AUTH.loginSubmitButton}
          disabled={loading}
          className="provi-auth-primary"
        >
          {loading ? 'Iniciando sesión...' : isProviderLogin ? 'Entrar al panel' : 'Iniciar sesión'}
        </button>

        <Link to={`/forgot-password?role=${isProviderLogin ? 'provider' : 'client'}`} className="provi-auth-minor-link">
          ¿Olvidaste tu contraseña?
        </Link>
      </form>

      <div className="provi-auth-links" style={{ marginTop: 18 }}>
        ¿No tienes una cuenta?{' '}
        <Link to={isProviderLogin ? '/register?role=provider' : '/register'}>Regístrate aquí</Link>
      </div>
    </AuthLayout>
  );
};

export default LoginPage;
